import ExcelJS from "exceljs";
import { ImportTaskData, RawTaskData, TasksByDate } from "../types/ImportTasksData";
import { Task } from "../types/WorkReportData";
import { WorkReportMeta } from "../types/WorkReportMeta";
import { NameID } from "common/types/NameID";
import { useWorkReport } from "../hooks/useWorkReport";
import { useWorkReportData } from "../hooks/useWorkReportData";
import { LastUpdatedInfo } from "common/types/LastUpdatedInfo";
import { WorkReportType } from "../types/WorkReportType";
import { TaskWithPeriod } from "../types/TaskWithPeriod";
import { toJapaneseDate } from "utils/DateTools";

export const useImportTasks = () => {
  const isInvalidDate = (date: Date) => Number.isNaN(date.getTime());
  const { getReportData, saveReportData } = useWorkReport();
  const { createEmptyReport } = useWorkReportData();

  /**
   * Workbookを生データとして読み込む「「
   * @param file
   */
  const readFile = async (file: File) => {
    const excelData = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(excelData);
    const { sheet, cells } = getNamedRange(workbook, "parkID");

    // 公園IDはセル縦2つ分（タイトルと実際の値）
    const parkId = +cells[1].text;
    const retVal: ImportTaskData = { parkId, tasksByDate: [] };

    // 公園IDのある次の行からデータがスタート
    const startRow = cells[1].row + 1;
    const startCol = cells[1].col + 1;

    // 日付はあれば開始、無ければ前の行と同じ
    let currTaskByDate: TasksByDate | undefined;
    let currRow = startRow;
    while (true) {
      let currCol = startCol;

      // 日付
      const dateCell = sheet.getCell(currRow, currCol);

      if (dateCell.text === "") {
        if (currTaskByDate === undefined) continue;
      } else {
        const date = new Date(dateCell.text);
        if (isInvalidDate(date)) continue;
        currTaskByDate = { date, tasks: [] };
        retVal.tasksByDate.push(currTaskByDate);
      }

      // 時間区分（無い場合はデータの終了とみなす）
      currCol += 1;
      const period = sheet.getCell(currRow, currCol).text;
      if (period === "") break;
      // 作業種別
      currCol += 1;
      const category = sheet.getCell(currRow, currCol).text;
      // 計画数量
      currCol += 1;
      const plannedAmount = +sheet.getCell(currRow, currCol).text;
      // 作業内容
      currCol += 1;
      const taskContent = sheet.getCell(currRow, currCol).text;
      // 事業者区分
      currCol += 1;
      const contractType = sheet.getCell(currRow, currCol).text;
      // 事業者名称
      currCol += 1;
      const contractorName = sheet.getCell(currRow, currCol).text;
      // 地区名称
      currCol += 1;
      const areas = sheet.getCell(currRow, currCol).text;

      currTaskByDate?.tasks.push({
        period,
        category,
        plannedAmount,
        taskContent,
        contractType,
        contractorName,
        areas,
      });

      currRow += 1;
    }

    return retVal;
  };

  /**
   * Workbookの名前付きセルを取得する
   */
  const getNamedRange = (
    workbook: ExcelJS.Workbook,
    definedName: string,
  ): {
    sheet: ExcelJS.Worksheet;
    cells: ExcelJS.Cell[];
  } => {
    const parkIdRange = workbook.definedNames.getRanges(definedName);
    if (parkIdRange.ranges.length === 0)
      throw new Error(`名前付きセル"${definedName}"がありません`);

    const sheetAndCells = parkIdRange.ranges[0].split("!");
    const sheetName = sheetAndCells[0];
    const cells = sheetAndCells[1].split(":").map((x) => x.replaceAll("$", ""));
    const sheet = workbook.getWorksheet(sheetName);
    if (sheet === undefined) throw new Error("シートが取得できませんでした");
    return { sheet, cells: cells.map((cell) => sheet.getCell(cell)) };
  };

  /**
   * 生データ1行に対し、対応するDBのメタデータに変換する
   * 不正な情報の場合はエラーとなる
   */
  const convert = (input: RawTaskData, meta: WorkReportMeta): { period: NameID; task: Task } => {
    const period = meta.periods.find((x) => x.name === input.period);
    if (!period) throw new Error(`時間区分が正しくありません`);

    const category = meta.workDefinitions.find((x) => x.name === input.category);
    if (!category) throw new Error("作業種別が正しくありません");
    const isCategoryValid =
      category.tabs === null ? true : category.tabs?.find((x) => x === period.name) !== undefined;
    if (!isCategoryValid) throw new Error("時間区分に対して作業種別が正しくありません");

    const contractType = meta.contractTypes.find((x) => x.name === input.contractType);
    if (!contractType) throw new Error("定義の無い事業者区分が正しくありません");

    const areas: NameID[] = [];
    input.areas
      .split(",")
      .map((val) => val.trim())
      .forEach((x) => {
        if (x === "") return;
        const area = meta.areas.find((y) => y.area.name === x);
        if (!area) throw new Error(`地区名称が正しくありません: ${x}`);
        areas.push(area.area);
      });

    return {
      period: { id: period.id ?? -1, name: period.name },
      task: {
        taskType: { id: category.id, name: category.name },
        plannedAmount: input.plannedAmount,
        taskContent: input.taskContent,
        contractType,
        contractorName: input.contractorName,
        areas,
        actualAmount: 0,
        unitOfTask: category.workUnit,
        pictures: [],
      },
    };
  };

  const importTasks = async (
    parkId: number,
    date: Date,
    tasks: (TaskWithPeriod | undefined)[],
    meta: WorkReportMeta,
    update: boolean,
    title: string, // 結果の文字列を作る時に使用
  ): Promise<{
    succeeded: boolean;
    msg: string;
    lastUpdated?: LastUpdatedInfo;
  }> => {
    const { succeeded, msg, data } = await getReportData(parkId, date, WorkReportType.ParkWork);
    if (!succeeded) {
      return { succeeded, msg };
    }

    const reports = data ? data.reports : meta.periods.map((period) => createEmptyReport(period));
    tasks.forEach((task) => {
      if (!task) return;
      // 時間帯を抽出
      var target = reports.find((x) => x.period.id === task.period.id);
      if (!target) return;

      if (update) {
        // 更新する対象があれば入れ替える
        const idx = target.tasks.findIndex((x) => x.taskType.id === task.task.taskType.id);
        if (idx >= 0) {
          target.tasks[idx] = task.task;
          return;
        }
        // 新規なら追加のフローへ
      }
      // 追加
      target.tasks.push(task.task);
    });

    try {
      return await saveReportData(parkId, date, { reports }, title, WorkReportType.ParkWork);
    } catch (error) {
      return { succeeded: false, msg: `${toJapaneseDate(date)}のデータの保存に失敗しました` };
    }
  };

  return { readFile, convert, importTasks };
};
